import { z } from 'zod';
import { SenseType, BatteryStatus } from '../../../types/index.js';
import { convertHexToDecimal, convertSignedHexToDecimal } from '../../../utils/index.js';
import { parseLabelStatuses, parseVersionTag, calculateRootMeanSquaredError, parseTemperatureDatapoints, interpolateSenseDatapoints } from '../helpers/index.js';
/**
 * Parsing schemas for TempSense labels
 */
const tempSenseSchema = {};
/**
 * TempSense schema version 1
 */
tempSenseSchema.v1 = z
    .tuple([
    z.string().max(5),
    z.string().max(14),
    z.string().max(8),
    z.string().max(6),
    z.string().max(5),
    z.string().max(4),
    z.string().max(320),
    z.string().max(2),
    z.string().max(4),
    z.string().max(3),
    z.string().max(2),
    z.string().max(8),
    z.string().max(4) // 12: checksum
])
    .transform((data) => ({
    versionTag: data[0],
    uid: data[1],
    startTime: data[2],
    elapsedTime: data[3],
    numberOfDatapointsAvailable: data[4],
    interval: data[5],
    datapoints: {
        temperature: data[6]
    },
    rangeStart: {
        temperature: data[7]
    },
    rootMeanSquaredError: {
        temperature: data[8]
    },
    currentValues: {
        temperature: data[9]
    },
    batteryStatus: data[10],
    statuses: data[11],
    checksum: data[12]
}))
    .transform((data) => {
    const LENGTH_OF_DOWNSAMPLED_SECTION = 280;
    const TEMPERATURE_RESOLUTION = 1;
    const preParsedData = {
        numberOfDatapointsAvailable: convertHexToDecimal(data.numberOfDatapointsAvailable),
        numberOfMeasurementsOccurred: convertHexToDecimal(data.numberOfDatapointsAvailable),
        rangeStart: {
            temperature: convertSignedHexToDecimal(data.rangeStart.temperature)
        },
        batteryStatus: data.batteryStatus.toLowerCase()
    };
    return {
        type: SenseType.TempSense,
        versionInfo: {
            firmwareVersion: parseVersionTag(data.versionTag).firmwareVersion
        },
        uid: data.uid.toLowerCase(),
        startTime: convertHexToDecimal(data.startTime),
        elapsedTime: convertHexToDecimal(data.elapsedTime),
        numberOfDatapointsAvailable: preParsedData.numberOfDatapointsAvailable,
        numberOfMeasurementsOccurred: preParsedData.numberOfMeasurementsOccurred,
        interval: convertHexToDecimal(data.interval),
        datapoints: {
            temperature: interpolateSenseDatapoints(parseTemperatureDatapoints(data.datapoints.temperature, preParsedData.rangeStart.temperature, TEMPERATURE_RESOLUTION), preParsedData.numberOfMeasurementsOccurred, LENGTH_OF_DOWNSAMPLED_SECTION)
        },
        rangeStart: preParsedData.rangeStart,
        rootMeanSquaredError: {
            temperature: calculateRootMeanSquaredError(convertHexToDecimal(data.rootMeanSquaredError.temperature))
        },
        currentValues: {
            temperature: convertSignedHexToDecimal(data.currentValues.temperature) / 10
        },
        batteryStatus: preParsedData.batteryStatus !== BatteryStatus.Undefined
            ? convertHexToDecimal(preParsedData.batteryStatus)
            : undefined,
        statuses: parseLabelStatuses(data.statuses),
        checksum: data.checksum
    };
});
/**
 * TempSense schema version 2
 */
tempSenseSchema.v2 = z
    .tuple([
    z.string().max(5),
    z.string().max(14),
    z.string().max(8),
    z.string().max(6),
    z.string().max(5),
    z.string().max(4),
    z.string().max(320),
    z.string().max(2),
    z.string().max(1),
    z.string().max(4),
    z.string().max(3),
    z.string().max(2),
    z.string().max(8),
    z.string().max(4) // 13: checksum
])
    .transform((data) => ({
    versionTag: data[0],
    uid: data[1],
    startTime: data[2],
    elapsedTime: data[3],
    numberOfDatapointsAvailable: data[4],
    interval: data[5],
    datapoints: {
        temperature: data[6]
    },
    rangeStart: {
        temperature: data[7]
    },
    resolution: {
        temperature: data[8]
    },
    rootMeanSquaredError: {
        temperature: data[9]
    },
    currentValues: {
        temperature: data[10]
    },
    batteryStatus: data[11],
    statuses: data[12],
    checksum: data[13]
}))
    .transform((data) => {
    const LENGTH_OF_DOWNSAMPLED_SECTION = 280;
    const preParsedData = {
        numberOfDatapointsAvailable: convertHexToDecimal(data.numberOfDatapointsAvailable),
        numberOfMeasurementsOccurred: convertHexToDecimal(data.numberOfDatapointsAvailable),
        rangeStart: {
            temperature: convertSignedHexToDecimal(data.rangeStart.temperature)
        },
        resolution: {
            temperature: convertHexToDecimal(data.resolution.temperature) / 10
        },
        batteryStatus: data.batteryStatus.toLowerCase()
    };
    return {
        type: SenseType.TempSense,
        versionInfo: {
            firmwareVersion: parseVersionTag(data.versionTag).firmwareVersion
        },
        uid: data.uid.toLowerCase(),
        startTime: convertHexToDecimal(data.startTime),
        elapsedTime: convertHexToDecimal(data.elapsedTime),
        numberOfDatapointsAvailable: preParsedData.numberOfDatapointsAvailable,
        numberOfMeasurementsOccurred: preParsedData.numberOfMeasurementsOccurred,
        interval: convertHexToDecimal(data.interval),
        datapoints: {
            temperature: interpolateSenseDatapoints(parseTemperatureDatapoints(data.datapoints.temperature, preParsedData.rangeStart.temperature, preParsedData.resolution.temperature), preParsedData.numberOfMeasurementsOccurred, LENGTH_OF_DOWNSAMPLED_SECTION)
        },
        rangeStart: preParsedData.rangeStart,
        resolution: preParsedData.resolution,
        rootMeanSquaredError: {
            temperature: calculateRootMeanSquaredError(convertHexToDecimal(data.rootMeanSquaredError.temperature))
        },
        currentValues: {
            temperature: convertSignedHexToDecimal(data.currentValues.temperature) / 10
        },
        batteryStatus: preParsedData.batteryStatus !== BatteryStatus.Undefined
            ? convertHexToDecimal(preParsedData.batteryStatus)
            : undefined,
        statuses: parseLabelStatuses(data.statuses),
        checksum: data.checksum
    };
});
export { tempSenseSchema };
